<template>
	<div class="r-calendar">
		<div class="r-calendar__header">
			<div class="r-calendar__heading">
				<span class="r-calendar__selected-month">
					{{ cursorDate.monthLong }}
				</span>
				<span class="r-calendar__selected-year">
					{{ cursorDate.year }}
				</span>
			</div>
			<nav class="r-calendar__navigation">
				<button
					class="r-calendar__navigation-button"
					@click="minusMonth"
				>
					<FontAwesomeIcon :icon="icons.faChevronLeft" />
				</button>
				<button
					class="r-calendar__navigation-button"
					@click="nextMonth"
				>
					<FontAwesomeIcon :icon="icons.faChevronRight" />
				</button>
			</nav>
		</div>

		<div class="r-calendar__body">
			<ul class="r-calendar__dates">
				<li
					v-for="name in weekDayNames"
					:key="name"
					class="r-calendar__cell r-calendar__cell--week-day-name"
				>
					{{ name }}
				</li>
				<li
					v-for="day in days"
					:key="`d:${day.day}-m:${day.month}`"
					class="r-calendar__cell r-calendar__cell--day"
					:class="[
						day.isToday && 'r-calendar__cell--today',
						day.inRange && 'r-calendar__cell--in-range',
						(day.isIntervalStart || day.isFirst) && 'r-calendar__cell--start',
						(day.isIntervalEnd || day.isLast) && 'r-calendar__cell--end',
						selectedRange.start.dt && day.equals(selectedRange.start.dt) && 'r-calendar__cell--selected',
						!day.inMonth && 'r-calendar__cell--disabled'
					]"
					@mouseenter="handleDayMouseOver(day)"
					@mouseleave="handleDayMouseOver(null)"
					@click="handleDayClick(day)"
				>
					<span>{{ day.day }}</span>
				</li>
			</ul>

			<ul class="r-calendar__quick-selects">
				<li
					v-for="preset in presets"
					:key="preset.id"
					class="r-calendar__quick-select-item"
				>
					<button @click="setDateIntervalFromPreset(preset)">
						{{ preset.name }}
					</button>
				</li>
			</ul>
		</div>

		<div class="r-calendar__footer">
			<!--  -->
		</div>
	</div>
</template>

<script>
import { DateTime, Interval, Info } from 'luxon'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons'

export default {
	name: 'RCalendar',
	components: {
		FontAwesomeIcon
	},
	props: {
		interval: {
			type: Object,
			default: null
		},
		presets: {
			type: Array,
			default: () => []
		}
	},
	data () {
		const todayDate = DateTime.fromJSDate(new Date()).startOf('day')
		return {
			icons: {
				faChevronLeft,
				faChevronRight
			},
			todayDate,
			cursorDate: todayDate,
			intervalDate: null,
			hoveredDate: null,
			selectedRange: {
				start: { dt: null },
				end: { dt: null }
			}
		}
	},
	computed: {
		weekDayNames () {
			return Info.weekdays('short')
		},
		days () {
			return this.getDaysInMonthMode(this.cursorDate)
		},
		isIntervalActive () {
			return this.intervalDate && !this.intervalDate.isEmpty()
		}
	},
	created () {
		this.intervalDate = this.interval
		if (this.intervalDate && this.intervalDate.start.equals(this.intervalDate.end)) {
			this.selectedRange.start.dt = this.intervalDate.start
		}
		if (this.intervalDate) {
			this.cursorDate = this.interval.end
		}
	},
	methods: {
		getDaysInMonthMode (dt) {
			const numberOfDays = Array(42)
			return numberOfDays.fill().map((_, index) => {
				const { todayDate, cursorDate, hoveredDate, intervalDate, isIntervalActive } = this
				const day = dt.startOf('month').startOf('week').plus({ days: index })

				day.isToday = day.equals(todayDate)
				day.inMonth = day.hasSame(cursorDate, 'month')
				day.inRange = false
				day.isIntervalStart = false
				day.isIntervalEnd = false
				day.isFirst = false
				day.isLast = false

				if (isIntervalActive) {
					day.isIntervalStart = day.equals(intervalDate.start)
					day.isIntervalEnd = day.hasSame(intervalDate.end, 'day')
					day.inRange = intervalDate.contains(day) && !day.isIntervalStart && !day.isIntervalEnd
				}

				if (hoveredDate && day.inMonth && hoveredDate > intervalDate.start) {
					if (intervalDate.start.equals(intervalDate.end)) {
						day.isLast = hoveredDate.equals(day)
						day.isFirst = day.equals(intervalDate.start)
					}
					if (!day.isLast && !day.isFirst && Interval.fromDateTimes(intervalDate.start, hoveredDate.plus({ days: 1 })).contains(day)) {
						day.inRange = true
					}
				}

				return day
			})
		},
		minusMonth () {
			this.cursorDate = this.cursorDate.minus({ months: 1 })
		},
		nextMonth () {
			this.cursorDate = this.cursorDate.plus({ months: 1 })
		},
		handleDayMouseOver (dt) {
			if (dt === null) {
				this.hoveredDate = null
				return
			}
			if (
				this.selectedRange.start.dt &&
				!this.selectedRange.end.dt &&
				dt > this.intervalDate.start &&
				!dt.equals(this.intervalDate.start)
			) {
				this.hoveredDate = dt
			}
		},
		handleDayClick (dt) {
			const start = this.selectedRange.start
			const end = this.selectedRange.end
			if (!start.dt) {
				this.hoveredDate = dt
				this.selectedRange.start.dt = dt
				this.intervalDate = Interval.fromDateTimes(dt, dt)
			} else {
				if (!end.dt) {
					this.hoveredDate = null
					this.selectedRange.end.dt = dt
					if (dt < start.dt) {
						this.intervalDate = Interval.fromDateTimes(dt, start.dt)
					} else {
						this.intervalDate = Interval.fromDateTimes(start.dt, dt)
					}
				} else {
					this.intervalDate = Interval.fromDateTimes(dt, dt)
					this.selectedRange.start.dt = dt
					this.hoveredDate = dt
					this.selectedRange.end.dt = null
				}
			}
			this.$emit('change', this.intervalDate)
		},
		setDateIntervalFromPreset (preset) {
			if (!this.cursorDate.hasSame(this.todayDate, 'month')) {
				this.cursorDate = this.todayDate
			}

			this.selectedRange.start.dt = null
			this.selectedRange.end.dt = null

			if (preset.range && preset.range.startDate && preset.range.endDate) {
				const startDate = DateTime.fromISO(preset.range.startDate)
				const endDate = DateTime.fromISO(preset.range.endDate)
				this.intervalDate = Interval.fromDateTimes(startDate, endDate)
				this.cursorDate = endDate
			} else {
				this.intervalDate = null
			}

			this.$emit('change', this.intervalDate)
			this.$emit('preset-selected', this.intervalDate)
		}
	}
}
</script>

<style lang="scss">
.r-calendar {
    box-sizing: border-box;
    max-width: 100vw;
    width: 500px;
    font-family: 'Open sans', sans-serif;
    padding: 15px 30px;
    background-color: getColor(surface-elevated);
    border-radius: 6px;
    box-shadow: 0 4px 12px 0 getColor(shadow) !important;

    @include media-breakpoint-down(sm) {
        width: calc(100vw - 32px);
        max-width: calc(100vw - 32px);
        padding: 15px;
    }

    @include dark-mode {
        border: 1px solid rgb(255, 255, 255, 0.10);
    }

    // =========================================================================
    // HEADER
    // =========================================================================
    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 15px 0;
    }

    &__heading {
        display: flex;
        gap: 6px;
        align-items: baseline;
    }

    &__selected-month,
    &__selected-year {
        font-size: 20px;
        font-weight: 600;
        text-transform: capitalize;
    }

    // =========================================================================
    // NAVIGATION BUTTONS — matches quarters-statistics__actions__item style
    // =========================================================================
    &__navigation {
        display: flex;
        gap: $gutter;

        &-button {
            appearance: none;
            border: 1px solid getColor(border-secondary);
            background-color: getColor(surface);
            color: getColor(icon);
            border-radius: 50%;
            width: 32px;
            height: 32px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 0;
            font-size: 16px;
            transition:
                background-color 0.15s ease,
                color 0.15s ease,
                border-color 0.15s ease;

            &:active,
            &:focus {
                outline: 0;
            }

            &:hover {
                cursor: pointer;
                background-color: getColor(hover-overlay);
                color: getColor(icon-active);
                border-color: getColor(border-common);
            }

            @include dark-mode {
                border-color: rgb(255, 255, 255, 0.14);

                &:hover {
                    border-color: color-mix(in srgb, #{getColor(primary)} 35%, transparent);
                    background-color: getColor(hover-overlay-strong);
                }
            }

            svg {
                color: inherit;
            }
        }
    }

    // =========================================================================
    // BODY
    // =========================================================================
    &__body {
        display: flex;
        width: 100%;
    }

    &__dates {
        display: flex;
        list-style: none;
        flex-wrap: wrap;
        margin: 0;
        padding: 0;
        width: 100%;
    }

    // =========================================================================
    // CELLS
    // =========================================================================
    &__cell {
        position: relative;
        box-sizing: border-box;
        flex-basis: calc(100% / 7);
        margin: 4px 0;
        height: 32px;
        line-height: 32px;
        vertical-align: middle;
        font-size: 14px;
        color: getColor(text);

        span {
            position: relative;
            z-index: 3;
        }

        &:nth-child(7n + 1) {
            margin-left: -10px;
        }

        &::after {
            border-radius: 100%;
            inset: 0;
            box-sizing: border-box;
            content: '';
            height: 32px;
            margin: auto;
            position: absolute;
            width: 32px;
            z-index: 2;
        }

        &--week-day-name {
            text-align: left;
            margin: 0;
            margin-left: 0 !important;
            pointer-events: none;
            color: getColor(helper-text);
            text-transform: capitalize;
        }

        &--day {
            text-align: center;
        }

        &--today {
            &::after {
                border: 1px solid getColor(text);

                @include dark-mode {
                    border: 1px solid transparentize(getColor(primary), 0.4);
                }
            }
        }

        &--selected {
            background: transparent;
            color: getColor(white);

            &::after {
                background-color: getColor(tertiary);
                border: 0;
            }
        }

        &--start {
            color: getColor(white);
            background: linear-gradient(90deg, transparent 50%, getColor(hover-overlay-solid) 50%);

            &::after {
                background-color: getColor(tertiary);
                border: 0;
            }
        }

        &--in-range {
            background: getColor(hover-overlay-solid);

            &::after {
                border: 0;
            }

            &:nth-child(7n) {
                border-top-right-radius: 100%;
                border-bottom-right-radius: 100%;
                background: linear-gradient(90deg, getColor(hover-overlay-solid) 50%, transparent 50%);

                &::after {
                    background: getColor(hover-overlay-solid);
                }
            }

            &:nth-child(7n + 1) {
                border-top-left-radius: 100%;
                border-bottom-left-radius: 100%;
                background: linear-gradient(90deg, transparent 50%, getColor(hover-overlay-solid) 50%);

                &::after {
                    background: getColor(hover-overlay-solid);
                }
            }
        }

        &--end {
            color: getColor(white);
            background: linear-gradient(90deg, getColor(hover-overlay-solid) 50%, transparent 50%);

            &::after {
                background-color: getColor(tertiary);
                border: 0;
            }
        }

        &--disabled {
            pointer-events: none;
            color: getColor(disabled-link);
            background: transparent !important;

            &::after {
                content: none;
            }
        }

        &:hover {
            cursor: pointer;
            color: getColor(text);

            &::after {
                background-color: getColor(surface);
                border: 2px solid getColor(tertiary);
            }
        }
    }

    // =========================================================================
    // QUICK SELECTS
    // =========================================================================
    &__quick-selects {
        display: flex;
        flex-flow: column;
        padding: 0;
        padding-left: 20px;
        border-left: 1px solid getColor(border);
        list-style: none;
        margin: 0;
        margin-top: 32px;

        @include media-breakpoint-down(sm) {
            display: none;
        }
    }

    &__quick-select-item {
        position: relative;
        box-sizing: border-box;
        margin: 4px 0;
        height: 32px;
        line-height: 32px;
        vertical-align: middle;
        font-size: 14px;
        color: getColor(text);
        white-space: nowrap;
        background-color: transparent;
    }

    &__quick-select-item button {
        box-sizing: border-box;
        appearance: none;
        border: 0;
        font-size: 14px;
        margin: 0;
        color: getColor(text);
        width: 100%;
        text-align: left;
        background-color: transparent;

        &:focus,
        &:active {
            outline: 0;
        }

        &:hover {
            color: getColor(tertiary);
            cursor: pointer;
        }
    }
}
</style>
